import React, { useEffect, useMemo, useState } from "react";
import {
  Text,
  useMantineTheme,
  Title,
  Table,
  Group,
  Skeleton,
  TextInput,
  ActionIcon,
  Collapse,
  Stack,
  Loader,
  Progress,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  IconChevronDown,
  IconChevronUp,
  IconSearch,
  IconSortAscending,
  IconSortDescending,
} from "@tabler/icons-react";
import { filterOrganizationsBySearch } from "./organizationSearch";
import PercentageIndicator from "../organization_dashboard/PageIndicatorComponent";
import TimePeriodSelector from "../common/TimePeriodSelector";
import { requestOrgBreakdown } from "@store/analytics/analyticsSlice";
import { selectOrgBreakdowns } from "@store/analytics/analyticsSelectors";
import { OrgBreakdown, OrgTestCountAnalytics, TimePeriod } from "types/analyticsTypes";

interface OrganizationTestCountsTableProps {
  data: OrgTestCountAnalytics[] | null;
  period: TimePeriod;
  onPeriodChange: (period: TimePeriod) => void;
  rangeActive?: boolean;
  startDate?: string;
  endDate?: string;
  onOrgClick?: (orgId: string) => void;
  expanded?: boolean;
  style?: React.CSSProperties;
}

const OrgBreakdownView: React.FC<{ breakdown?: OrgBreakdown }> = ({ breakdown }) => {
  const { t } = useTranslation();

  if (!breakdown) {
    return (
      <Group justify="center" p="md">
        <Loader size="sm" />
      </Group>
    );
  }

  if (breakdown.devices.length === 0 && breakdown.categories.length === 0) {
    return (
      <Text c="dimmed" size="sm" p="sm">
        {t("analytics.seeMore.noData")}
      </Text>
    );
  }

  return (
    <Group gap="xl" p="sm" align="flex-start">
      <Stack gap="xs">
        <Text fw={600} size="sm">
          {t("analytics.seeMore.devicesTitle")}
        </Text>
        {breakdown.devices.map((device) => (
          <Group key={device.device} justify="space-between" gap="xl">
            <Text size="sm">{device.device}</Text>
            <Text size="sm" fw={600}>
              {device.count}
            </Text>
          </Group>
        ))}
      </Stack>
      <Stack gap="xs">
        <Text fw={600} size="sm">
          {t("analytics.seeMore.categoriesTitle")}
        </Text>
        {breakdown.categories.map((category) => (
          <Group key={category.category} justify="space-between" gap="xl">
            <Text size="sm">
              {t(`TestType.${category.category}`, { defaultValue: category.category })}
            </Text>
            <Text size="sm" fw={600}>
              {category.count}
            </Text>
          </Group>
        ))}
      </Stack>
    </Group>
  );
};

const OrganizationTestCountsTable: React.FC<OrganizationTestCountsTableProps> = ({
  data,
  period,
  onPeriodChange,
  rangeActive,
  startDate,
  endDate,
  onOrgClick,
  expanded,
  style,
}) => {
  const { t } = useTranslation();
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const theme = useMantineTheme();
  const dispatch = useDispatch();
  const orgBreakdowns = useSelector(selectOrgBreakdowns);
  const effectiveField = rangeActive ? TimePeriod.RANGE : period;

  const expandedBreakdown = expandedOrgId ? orgBreakdowns[expandedOrgId] : undefined;

  useEffect(() => {
    if (expandedOrgId && !expandedBreakdown) {
      dispatch(requestOrgBreakdown({ orgId: expandedOrgId, startDate, endDate }));
    }
  }, [expandedOrgId, expandedBreakdown, startDate, endDate, dispatch]);

  useEffect(() => {
    if (expandedOrgId && data && !data.some((org) => org.orgId === expandedOrgId)) {
      setExpandedOrgId(null);
    }
  }, [data, expandedOrgId]);

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return [...filterOrganizationsBySearch(data, search)].sort((a, b) => {
      const aValue = a[effectiveField]?.count ?? 0;
      const bValue = b[effectiveField]?.count ?? 0;
      return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
    });
  }, [data, search, effectiveField, sortDirection]);

  const maxCount = useMemo(
    () => Math.max(1, ...sortedData.map((org) => org[effectiveField]?.count ?? 0)),
    [sortedData, effectiveField],
  );

  const rankingKey = sortedData.map((org) => org.orgId).join("|");

  useEffect(() => {
    if (!expandedOrgId || expanded) return;
    document
      .getElementById(`org-row-${expandedOrgId}`)
      ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [rankingKey, expandedOrgId, expanded]);

  const toggleExpanded = (orgId: string) => {
    setExpandedOrgId(expandedOrgId === orgId ? null : orgId);
  };

  return (
    <Skeleton visible={!data} style={style}>
      <Group justify="space-between" mb="md" wrap="wrap" gap="sm">
        <Title order={3}>{t("analytics.organizations.title")}</Title>
        {!expanded && (
          <Group gap="xs">
            {!rangeActive && <TimePeriodSelector value={period} onChange={onPeriodChange} />}
            <ActionIcon
              onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}
              variant="subtle"
              title={t(`analytics.sort.${sortDirection === "asc" ? "ascending" : "descending"}`)}
              aria-label={t(
                `analytics.sort.${sortDirection === "asc" ? "ascending" : "descending"}`,
              )}
            >
              {sortDirection === "asc" ? <IconSortAscending /> : <IconSortDescending />}
            </ActionIcon>
          </Group>
        )}
      </Group>
      {!expanded && data && data.length > 0 && (
        <TextInput
          mb="md"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder={t("analytics.organizations.searchPlaceholder")}
          aria-label={t("analytics.organizations.searchPlaceholder")}
          leftSection={<IconSearch size={16} />}
        />
      )}
      {data && data.length === 0 ? (
        <Text c="dimmed">{t("analytics.organizations.noData")}</Text>
      ) : data && sortedData.length === 0 ? (
        <Text c="dimmed">{t("analytics.organizations.noSearchResults")}</Text>
      ) : (
        <Table.ScrollContainer minWidth={480} maxHeight={expanded ? undefined : 760}>
          <Table highlightOnHover withRowBorders>
            <Table.Tbody>
              {sortedData.map((orgData) => {
                const isExpanded = expanded || expandedOrgId === orgData.orgId;
                const stats = orgData[effectiveField];
                const count = stats?.count ?? 0;
                return (
                  <React.Fragment key={orgData.orgId}>
                    <Table.Tr
                      id={`org-row-${orgData.orgId}`}
                      bg={isExpanded && !expanded ? "var(--mantine-color-blue-light)" : undefined}
                    >
                      <Table.Td w="30%">
                        <Tooltip label={`ID: ${orgData.orgId}`} position="top-start" withArrow>
                          <UnstyledButton onClick={() => onOrgClick && onOrgClick(orgData.orgId)}>
                            <Text size="sm" fw={500} c={theme.primaryColor} lineClamp={1}>
                              {orgData.orgName}
                            </Text>
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {orgData.orgId}
                            </Text>
                          </UnstyledButton>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td w="45%">
                        <Progress value={(count / maxCount) * 100} size="md" radius="xl" />
                      </Table.Td>
                      <Table.Td align="right">
                        <PercentageIndicator
                          count={count}
                          previousCount={stats?.previousCount ?? 0}
                          percentageChange={stats?.percentageChange ?? 0}
                          useColorToDisplayCount
                        />
                      </Table.Td>
                      <Table.Td w={48}>
                        {!expanded && (
                          <ActionIcon
                            variant="subtle"
                            onClick={() => toggleExpanded(orgData.orgId)}
                            title={t("analytics.seeMore.toggle")}
                            aria-label={t("analytics.seeMore.toggle")}
                          >
                            {isExpanded ? <IconChevronUp /> : <IconChevronDown />}
                          </ActionIcon>
                        )}
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td
                        colSpan={4}
                        p={0}
                        style={{ border: isExpanded ? undefined : "none" }}
                      >
                        {expanded ? (
                          <OrgBreakdownView breakdown={orgBreakdowns[orgData.orgId]} />
                        ) : (
                          <Collapse in={isExpanded}>
                            <OrgBreakdownView breakdown={orgBreakdowns[orgData.orgId]} />
                          </Collapse>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  </React.Fragment>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Skeleton>
  );
};

export default OrganizationTestCountsTable;
